import { useState, useEffect, useRef } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import {
  LayoutDashboard, Users, Kanban, Mail,
  FileText, Settings, Shield, Bot, Globe,
  Building2, X, Menu,
  Wallet, Building, Scale, Receipt, Gavel,
  CheckSquare, TrendingUp, FolderOpen, Sparkles,
  ClipboardCheck, CalendarDays, BookOpen, Moon, Sun,
  Plug, MessageSquareText, Search, LogOut, ClipboardList,
} from 'lucide-react'
import { useT, useI18n } from '../i18n/index.js'
import { useRole, type Role } from '../hooks/useRole.js'
import { useTheme } from '../hooks/useTheme.js'
import { GlobalSearch, openGlobalSearch } from './GlobalSearch.js'
import { AssistantFab } from './voice/AssistantFab.js'
import { NotificationPanel } from './NotificationPanel.js'

// -- Nav section config --

interface NavItem {
  path: string
  labelKey: string
  icon: React.ElementType
}

interface NavSection {
  id: string
  label: string
  roles: Role[] | 'ALL' | 'ALL_EXCEPT_PORTAL'
  items: NavItem[]
}

/** Sections visibles selon le role courant */
const NAV_SECTIONS: NavSection[] = [
  {
    id: 'principal',
    label: 'PRINCIPAL',
    roles: 'ALL',
    items: [
      { path: '/dashboard', labelKey: 'nav.dashboard', icon: LayoutDashboard },
      { path: '/mailbox', labelKey: 'nav.mailbox', icon: Mail },
    ],
  },
  {
    id: 'crm',
    label: 'CRM',
    roles: ['AGENT', 'AGENCY_ADMIN', 'COLLABORATOR'],
    items: [
      { path: '/contacts', labelKey: 'nav.contacts', icon: Users },
      { path: '/mandats', labelKey: 'nav.mandats', icon: FileText },
      { path: '/pipeline', labelKey: 'nav.pipeline', icon: Kanban },
      { path: '/agenda', labelKey: 'nav.agenda', icon: CalendarDays },
      { path: '/tasks', labelKey: 'nav.tasks', icon: CheckSquare },
      { path: '/inspections', labelKey: 'nav.inspections', icon: ClipboardList },
    ],
  },
  {
    id: 'proprio',
    label: 'GESTION LOCATIVE',
    roles: ['AGENT', 'OWNER'],
    items: [
      { path: '/proprio', labelKey: 'nav.proprio', icon: Building },
      { path: '/budget', labelKey: 'nav.budgetCharges', icon: Receipt },
    ],
  },
  {
    id: 'syndic',
    label: 'SYNDIC',
    roles: ['SYNDIC', 'AGENCY_ADMIN'],
    items: [
      { path: '/syndic', labelKey: 'nav.syndicCoproprietes', icon: Building2 },
      { path: '/syndic/ag', labelKey: 'nav.syndicAssemblees', icon: Gavel },
    ],
  },
  {
    id: 'finance',
    label: 'FINANCE',
    roles: 'ALL_EXCEPT_PORTAL',
    items: [
      { path: '/budget', labelKey: 'nav.budgetFinance', icon: Wallet },
      { path: '/invest', labelKey: 'nav.invest', icon: TrendingUp },
    ],
  },
  {
    id: 'notaire',
    label: 'NOTAIRE',
    roles: ['NOTAIRE'],
    items: [
      { path: '/notaire', labelKey: 'nav.notaireEstimations', icon: Scale },
      { path: '/notaire/checklists', labelKey: 'nav.notaireChecklists', icon: ClipboardCheck },
    ],
  },
  {
    id: 'outils',
    label: 'OUTILS',
    roles: 'ALL_EXCEPT_PORTAL',
    items: [
      { path: '/documents', labelKey: 'nav.documents', icon: FolderOpen },
      { path: '/templates', labelKey: 'nav.templates', icon: MessageSquareText },
      { path: '/aria', labelKey: 'nav.aria', icon: Sparkles },
      { path: '/integrations', labelKey: 'nav.integrations', icon: Plug },
      { path: '/glossaire', labelKey: 'nav.glossaire', icon: BookOpen },
    ],
  },
  {
    id: 'admin',
    label: 'ADMIN',
    roles: ['AGENCY_ADMIN'],
    items: [
      { path: '/admin', labelKey: 'nav.admin', icon: Settings },
    ],
  },
]

const PORTAL_ROLES: Role[] = ['CLIENT', 'LOCATAIRE']

function isSectionVisible(section: NavSection, role: Role): boolean {
  if (section.roles === 'ALL') return true
  if (section.roles === 'ALL_EXCEPT_PORTAL') return !PORTAL_ROLES.includes(role)
  return section.roles.includes(role)
}

// -- Nav label resolver --

// Fallback labels (should not be needed if i18n keys are correct)
const LABEL_MAP: Record<string, string> = {}

function resolveLabel(t: ReturnType<typeof useT>, key: string): string {
  // Try i18n first via dot-path
  const parts = key.split('.')
  let val: unknown = t
  for (const p of parts) {
    if (val && typeof val === 'object' && p in (val as Record<string, unknown>)) {
      val = (val as Record<string, unknown>)[p]
    } else {
      val = undefined
      break
    }
  }
  if (typeof val === 'string') return val
  // Fallback to hardcoded map
  return LABEL_MAP[key] || key
}

// -- Role switcher (DEV only) --

const ROLE_LIST: Role[] = ['AGENT', 'AGENCY_ADMIN', 'COLLABORATOR', 'OWNER', 'SYNDIC', 'NOTAIRE', 'CLIENT', 'LOCATAIRE', 'SUPPORT', 'PLATFORM_ADMIN']

const ROLE_COLORS: Record<Role, string> = {
  AGENT: 'var(--color-primary)',
  AGENCY_ADMIN: '#4F46E5',
  COLLABORATOR: '#D97706',
  CLIENT: '#059669',
  LOCATAIRE: '#0EA5E9',
  OWNER: '#7C3AED',
  SYNDIC: '#DC2626',
  NOTAIRE: '#44403C',
  SUPPORT: '#6B7280',
  PLATFORM_ADMIN: '#0F172A',
  COMPLIANCE_OFFICER: '#0891B2',
}

// -- Components --

interface AppLayoutProps {
  children: React.ReactNode
}

interface CurrentUser { firstName?: string; lastName?: string; email?: string; role?: string; tenantId?: string }

export function AppLayout({ children }: AppLayoutProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const t = useT()
  const { locale, setLocale } = useI18n()
  const { role, setRole } = useRole()
  const { isDark, toggle: toggleTheme } = useTheme()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [supportTenantName, setSupportTenantName] = useState<string | null>(null)
  const userMenuRef = useRef<HTMLDivElement>(null)
  const sidebarDialogRef = useRef<HTMLElement>(null)

  useEffect(() => {
    fetch('/api/auth/me', { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then((u: CurrentUser | null) => {
        if (!u) return
        setCurrentUser(u)
        // Applique le rôle réel depuis le serveur — le défaut AGENT dans useRoleProvider est un fallback dev
        if (u.role) setRole(u.role as Role)
      })
      .catch(() => {})
  }, [])

  // Résoudre le nom du tenant courant pour le SUPPORT
  useEffect(() => {
    if (role !== 'SUPPORT' || !currentUser?.tenantId) return
    fetch('/api/support/tenants', { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then((res: { data: { _id: string; name: string }[] } | null) => {
        if (!res?.data) return
        const t = res.data.find(t => t._id === currentUser.tenantId)
        if (t) setSupportTenantName(t.name)
      })
      .catch(() => {})
  }, [role, currentUser?.tenantId])

  useEffect(() => {
    if (!userMenuOpen) return
    function handler(e: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [userMenuOpen])

  async function handleLogout() {
    await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' })
    navigate('/login', { replace: true })
  }

  function userInitial(): string {
    if (currentUser?.firstName) return currentUser.firstName[0].toUpperCase()
    if (currentUser?.email) return currentUser.email[0].toUpperCase()
    return '?'
  }

  function userDisplayName(): string {
    if (currentUser?.firstName && currentUser?.lastName) {
      return `${currentUser.firstName} ${currentUser.lastName}`
    }
    return currentUser?.email ?? ''
  }

  const visibleSections = NAV_SECTIONS.filter(s => isSectionVisible(s, role))
  const isPortalRole = PORTAL_ROLES.includes(role)

  useEffect(() => {
    if (!sidebarOpen) return
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setSidebarOpen(false) }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [sidebarOpen])

  useEffect(() => {
    if (!sidebarOpen) return
    const dialog = sidebarDialogRef.current
    if (!dialog) return
    const focusables = dialog.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )
    const first = focusables[0]
    const last = focusables[focusables.length - 1]
    first?.focus()
    function onTab(e: KeyboardEvent) {
      if (e.key !== 'Tab') return
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last?.focus() }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first?.focus() }
      }
    }
    document.addEventListener('keydown', onTab)
    return () => document.removeEventListener('keydown', onTab)
  }, [sidebarOpen])

  function isActive(path: string): boolean {
    // Exact match or prefix match (but /budget must not match /budget-charges)
    if (location.pathname === path) return true
    if (path === '/budget' && location.pathname === '/budget') return true
    // For nested routes like /budget/perso, /contacts/:id etc
    if (location.pathname.startsWith(path + '/')) return true
    return false
  }

  function renderNavSections(options?: { onLinkClick?: () => void }) {
    return visibleSections.map((section, sIdx) => (
      <div key={section.id}>
        {sIdx > 0 && (
          <div className="mx-4 border-t border-white/10" />
        )}
        <div className="px-4 pt-4 pb-1">
          <span className="text-[10px] font-medium tracking-wider uppercase text-white/40">
            {resolveLabel(t, `nav.groups.${section.id}`) || section.label}
          </span>
        </div>
        <div className="pb-1">
          {section.items.map((item) => {
            const Icon = item.icon
            const active = isActive(item.path)
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={options?.onLinkClick}
                className={`flex items-center gap-3 mx-2 px-3 py-2 min-h-[44px] rounded-md text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-decorative)] focus-visible:ring-offset-1 focus-visible:ring-offset-[var(--color-primary)] ${
                  active
                    ? 'border-l-2 border-[var(--color-accent-decorative)] text-[var(--color-accent-decorative)] font-medium pl-[10px]'
                    : 'text-white/70 hover:bg-white/[0.06] hover:text-white'
                }`}
              >
                <Icon size={18} />
                <span>{resolveLabel(t, item.labelKey)}</span>
              </Link>
            )
          })}
        </div>
      </div>
    ))
  }

  function renderRoleSwitcher() {
    if (!import.meta.env.DEV) return null
    return (
      <div className="px-4 py-3 border-t border-white/10">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-white/40 mb-2">
          DEV — Role
        </p>
        <div className="flex flex-wrap gap-1">
          {ROLE_LIST.map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setRole(r)}
              className="px-2 py-1 rounded text-[10px] font-medium border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-decorative)]"
              style={{
                borderColor: role === r ? ROLE_COLORS[r] : 'rgba(255,255,255,0.15)',
                backgroundColor: role === r ? `${ROLE_COLORS[r]}33` : 'transparent',
                color: role === r ? ROLE_COLORS[r] : 'rgba(255,255,255,0.4)',
              }}
              aria-pressed={role === r}
            >
              {r.replace('_', ' ')}
            </button>
          ))}
        </div>
      </div>
    )
  }

  function renderPortalNav(options?: { onLinkClick?: () => void }) {
    return (
      <div className="px-4 py-6 text-center">
        <p className="text-xs text-white/60 mb-3">
          {role === 'CLIENT' ? t.nav.portal.espaceClient : t.nav.portal.espaceLocataire}
        </p>
        <Link
          to={role === 'CLIENT' ? '/portail/client' : '/portail/locataire'}
          onClick={options?.onLinkClick}
          className="inline-flex items-center gap-2 px-4 min-h-[44px] rounded-lg bg-[var(--color-primary)] text-white text-sm font-medium hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)]"
        >
          {t.nav.portal.acceder}
        </Link>
      </div>
    )
  }

  // Bandeau support OBO (on-behalf-of) — visible uniquement si role=SUPPORT avec tenant sélectionné
  function renderSupportBanner() {
    if (role !== 'SUPPORT' || !currentUser?.tenantId) return null
    return (
      <div className="mx-3 mb-2 flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-800">
        <Shield size={14} className="shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-wide">Mode support</p>
          <p className="text-xs truncate">{supportTenantName ?? currentUser.tenantId}</p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/support/tenant-picker')}
          className="text-[10px] font-medium text-amber-700 hover:text-amber-900 underline hover:no-underline shrink-0 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-amber-700"
          aria-label="Changer de tenant support"
        >
          Changer
        </button>
      </div>
    )
  }

  return (
    <div className="flex h-screen bg-[var(--color-background)]">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:top-2 focus:left-2 focus:rounded-md focus:bg-[var(--color-primary)] focus:px-4 focus:py-2 focus:text-white"
      >
        Aller au contenu principal
      </a>

      {/* Sidebar — desktop */}
      <aside className="hidden lg:flex lg:w-64 lg:flex-col bg-[var(--color-primary)]">
        <div className="flex h-14 items-center px-6 border-b border-white/10">
          <Link to="/dashboard" className="font-[family-name:var(--font-heading)] italic text-lg font-bold text-[var(--color-accent-decorative)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-decorative)] focus-visible:ring-offset-1 focus-visible:ring-offset-[var(--color-primary)]">
            {t.common.appName}
          </Link>
        </div>
        {renderSupportBanner()}
        <nav className="flex-1 overflow-y-auto py-1" role="navigation" aria-label="Navigation principale">
          {isPortalRole ? renderPortalNav() : renderNavSections()}
        </nav>
        {/* Role switcher en bas */}
        {renderRoleSwitcher()}
        <div className="border-t border-white/10 p-4">
          <div className="flex items-center justify-between">
            <span className="text-xs text-white/40">
              {t.common.appName} {t.common.version}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={toggleTheme}
                className="min-h-[44px] min-w-[44px] flex items-center justify-center text-xs text-white/50 hover:text-white/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-decorative)] focus-visible:ring-offset-1 focus-visible:ring-offset-[var(--color-primary)]"
                aria-label={isDark ? 'Passer en mode clair' : 'Passer en mode sombre'}
                title={isDark ? 'Mode clair' : 'Mode sombre'}
              >
                {isDark ? <Sun size={14} /> : <Moon size={14} />}
              </button>
              <button
                onClick={() => setLocale(locale === 'fr' ? 'nl' : 'fr')}
                className="min-h-[44px] min-w-[44px] flex items-center justify-center gap-1 text-xs text-white/50 hover:text-white/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-decorative)] focus-visible:ring-offset-1 focus-visible:ring-offset-[var(--color-primary)]"
                aria-label="Changer de langue"
                title="Changer de langue"
              >
                <Globe size={14} />
                {locale.toUpperCase()}
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 lg:hidden" onClick={() => setSidebarOpen(false)}>
          <div className="absolute inset-0 bg-black/50" aria-hidden="true" />
          <aside
            ref={sidebarDialogRef}
            className="relative w-64 h-full bg-[var(--color-primary)] shadow-lg overflow-y-auto"
            role="dialog"
            aria-modal="true"
            aria-label="Menu de navigation"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex h-14 items-center justify-between px-6 border-b border-white/10">
              <span className="font-[family-name:var(--font-heading)] italic text-lg font-bold text-[var(--color-accent-decorative)]">{t.common.appName}</span>
              <button
                onClick={() => setSidebarOpen(false)}
                aria-label="Fermer le menu"
                className="text-white/50 hover:text-white/80 min-h-[44px] min-w-[44px] flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-decorative)] focus-visible:ring-offset-1 focus-visible:ring-offset-[var(--color-primary)]"
              >
                <X size={18} />
              </button>
            </div>
            {renderSupportBanner()}
            <nav className="py-1" role="navigation" aria-label="Navigation mobile">
              {isPortalRole
                ? renderPortalNav({ onLinkClick: () => setSidebarOpen(false) })
                : renderNavSections({ onLinkClick: () => setSidebarOpen(false) })
              }
            </nav>
            {renderRoleSwitcher()}
          </aside>
        </div>
      )}

      {/* Main content */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Topbar */}
        <header className="flex h-14 items-center justify-between border-b border-[var(--color-border)] bg-[var(--color-card)] px-4 lg:px-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen(true)}
              aria-label="Ouvrir le menu"
              className="lg:hidden min-h-[44px] min-w-[44px] flex items-center justify-center text-[var(--color-text-secondary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)] focus-visible:ring-offset-1"
            >
              <Menu size={20} />
            </button>
            <Breadcrumb />
          </div>
          <div className="flex items-center gap-2">
            {import.meta.env.DEV && (
              <span className="text-[10px] font-medium px-2 py-1 rounded bg-[var(--color-surface-raised)] text-[var(--color-text-disabled)] border border-[var(--color-border-subtle)]">
                {role}
              </span>
            )}
            <button
              onClick={openGlobalSearch}
              aria-label="Recherche globale"
              className="min-h-[44px] min-w-[44px] flex items-center justify-center text-[var(--color-text-secondary)] hover:text-[var(--color-text)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)] focus-visible:ring-offset-1"
            >
              <Search size={18} />
            </button>
            <NotificationPanel />
            <div ref={userMenuRef} className="relative">
              <button
                type="button"
                onClick={() => setUserMenuOpen(v => !v)}
                className="h-8 w-8 rounded-full bg-[var(--color-primary)] flex items-center justify-center text-white text-xs font-medium hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)] focus-visible:ring-offset-1"
                aria-label="Menu utilisateur"
                aria-expanded={userMenuOpen}
              >
                {userInitial()}
              </button>
              {userMenuOpen && (
                <div className="absolute right-0 top-full mt-2 w-52 bg-[var(--color-surface)] rounded-xl border border-[var(--color-border)] shadow-2xl z-50 py-1">
                  <div className="px-4 py-2 border-b border-[var(--color-border-subtle)]">
                    <p className="text-xs font-medium text-[var(--color-text)] truncate">{userDisplayName()}</p>
                    {currentUser?.email && (
                      <p className="text-[10px] text-[var(--color-text-secondary)] truncate">{currentUser.email}</p>
                    )}
                  </div>
                  <Link
                    to="/settings"
                    onClick={() => setUserMenuOpen(false)}
                    className="flex items-center gap-2 px-4 py-2 text-sm text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-raised)] hover:text-[var(--color-text)]"
                  >
                    <Settings size={14} />
                    {t.nav.settings}
                  </Link>
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-[var(--color-danger,#DC2626)] hover:bg-[var(--color-surface-raised)]"
                  >
                    <LogOut size={14} />
                    {t.common.logout}
                  </button>
                </div>
              )}
            </div>
          </div>
          <GlobalSearch />
        </header>

        {/* Page content */}
        <main id="main-content" className="flex-1 overflow-y-auto">
          {children}
        </main>
      </div>

      {/* Assistant FAB — voice + chat Aria */}
      <AssistantFab />
    </div>
  )
}

function Breadcrumb() {
  const location = useLocation()
  const t = useT()
  const segments = location.pathname.split('/').filter(Boolean)

  const bc = t.nav.breadcrumb
  const labels: Record<string, string> = {
    dashboard: t.nav.dashboard,
    contacts: t.nav.contacts,
    mandats: t.nav.mandats,
    pipeline: t.nav.pipeline,
    mailbox: t.nav.mailbox,
    documents: t.nav.documents,
    settings: t.nav.settings,
    admin: t.nav.admin,
    aria: t.nav.aria,
    proprio: bc.proprio,
    biens: bc.biens,
    baux: bc.baux,
    loyers: bc.loyers,
    edl: bc.edl,
    indexation: bc.indexation,
    signalements: bc.signalements,
    preavis: bc.preavis,
    budget: bc.budget,
    perso: bc.perso,
    pro: bc.pro,
    patrimoine: bc.patrimoine,
    transactions: bc.transactions,
    reconciliation: bc.reconciliation,
    invest: bc.invest,
    syndic: bc.syndic,
    ag: bc.ag,
    notaire: bc.notaire,
    checklists: bc.checklists,
    'budget-charges': bc.budgetCharges,
    agenda: bc.agenda,
    tasks: bc.tasks,
    glossaire: bc.glossaire,
    onboarding: bc.onboarding,
  }

  return (
    <nav className="flex items-center gap-1 text-sm text-[var(--color-text-secondary)]">
      {segments.map((segment, i) => (
        <span key={i} className="flex items-center gap-1">
          {i > 0 && <span>/</span>}
          <span className={i === segments.length - 1 ? 'text-[var(--color-text)] font-medium' : ''}>
            {labels[segment] || segment}
          </span>
        </span>
      ))}
    </nav>
  )
}
